/* eslint-disable react/no-array-index-key */
/* eslint-disable react/require-default-props */
import {
  FC, useMemo, useState, useEffect, useCallback, type MouseEvent,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useAuth } from "../../../modules/auth/useAuth";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  Document, Defend, AIAction, User, AIChat, Lock,
} from "@xfrontend/xicon";
import {
  Accordion,
  TableV8,
  Dropdown,
  Tooltip,
} from "@xfrontend/xui-react-plus";
import {
  MaliciousRequest, POLICY_DISPLAY_NAMES, POLICY_RISK_LEVEL, POLICY_TAG_COLOR_INDEX, RISK_LEVEL_MAP,
} from "#/types/xecguard";
import { InternalPolicyItem } from "#/apis/infra/getPolicies";
import { RootState } from "#/types/RootState";
import { ServiceTokenType } from "#/apis/infra/getServiceTokens";
import {
  TraceMessage, TraceViolationItem, EvidenceItem,
} from "#/apis/infra/getTraceDetail";
import HighlightedText, { HighlightRange } from "#/modules/common/HighlightedText";
import { getTraceDetail, getTraceRecords, resetTraceRecords, getStatsSummary } from "#/modules/dashboard/redux/actionCreator";
import { formatTimestamp } from "#/utils/analytics";
import { FilterComponentProps, TableCellInfo } from "../types";
import RecentUnsafeCount from "./RecentUnsafeCount";
import HeaderSelectFilter from "./HeaderSelectFilter";
import InfiniteLoader from "#/modules/common/InfiniteLoader";
import CommonModal from "#/modules/common/CommonModal";
import ViolationPolicyTypeTag from "#/modules/common/ViolationPolicyTypeTag";
import RiskLevelTag from "#/modules/common/RiskLevelTag";

export const SectionHeader = styled.div`
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 10px;
`;

export const TraceTableWrapper = styled.div`
  background-color: var(--x-bg-500, #2d293a);
  border-radius: 8px;
  padding: 12px;

  .no-caret .dropdown-toggle::after {
    display: none;
  }
`;

export const Tag = styled.span`
  background-color: rgba(235, 87, 87, 0.2);
  color: #eb5757;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  margin-right: 8px;
`;

export const IconAction = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 2px;
  padding: 2px;
  gap: 10px;
  transform: rotate(0deg);
  opacity: 0.7;
`;

const IconWrapper = styled.div`
  width: 24px;
  height: 24px;
  background-color: var(--x-purple-700);
  border-radius: 4px;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TraceModal = styled(CommonModal)`
  .modal-dialog {
    width: 900px;
    max-width: calc(100% - 32px);
  }

  .modal-content {
    background: var(--x-bg-500, #2d293a);
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0px 6px 24px rgba(0, 0, 0, 0.4);
  }

  .modal-header {
    background: var(--x-bg-400, #3b3649);
    padding: 16px;
  }

  .modal-body {
    padding: 0;
  }

  .modal-footer {
    padding: 16px;
  }
`;

const ModalSection = styled.div`
  padding: 18px 24px;
  border-bottom: 1px solid var(--x-theme-secondary, #73698a);
`;

const ModalTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  line-height: 1.4;
  color: var(--x-gray-200, #f6f6f7);
`;

const ModalSubRow = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  margin-top: 12px;
`;

const ModalSubText = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: var(--x-gray-200, #f6f6f7);
`;

const SectionHeading = styled.div`
  font-size: 14px;
  line-height: 1;
  color: var(--x-purple-300, #8e86a3);
  padding: 18px 24px 9px;
`;

const KeyValueRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const KeyLabel = styled.div`
  min-width: 110px;
  flex-shrink: 0;
  white-space: nowrap;
  font-size: 14px;
  line-height: 1.5;
  color: var(--x-gray-500, #a9a9ac);
`;

const ValueText = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: var(--x-gray-200, #f6f6f7);
`;

const MonoText = styled(ValueText)`
  font-family: "Roboto Mono", sans-serif;
`;

const ViolationCard = styled.div`
  background: var(--x-purple-600, #3a3646);
  border-radius: 4px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ViolationHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ViolationTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  line-height: 1.5;
  color: var(--x-gray-200, #f6f6f7);
`;

const ViolationDetailRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const TimelineWrap = styled.div`
  background: var(--x-dark-dark, #191423);
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TimelineItem = styled.div`
  display: flex;
  gap: 6px;
  align-items: flex-start;
`;

const TimelineIcon = styled.div<{
  tone: "system" | "user" | "assistant" | "blocked";
}>`
  width: 22px;
  height: 22px;
  border-radius: 34px;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${(props: {
    tone: "system" | "user" | "assistant" | "blocked";
  }) => {
    if (props.tone === "user") return "var(--x-theme-info, #00aab6)";
    if (props.tone === "assistant") return "var(--x-theme-warning, #846ce6)";
    if (props.tone === "blocked") return "var(--x-theme-warning, #846ce6)";
    return "var(--x-theme-secondary, #73698a)";
  }};
`;

const TimelineContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const TimelineRole = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: var(--x-gray-200, #f6f6f7);
`;

const HistoryAccordion = styled(Accordion)`
  .accordion-item {
    border: 0;
    background: transparent;
  }

  .accordion-button {
    padding: 0;
    width: 100%;
    display: flex;
    align-items: center;
    background: var(--x-purple-500, #494358);
    color: inherit;
    box-shadow: none;
    border-radius: 4px;
    padding-right: 12px;
  }

  .accordion-button:not(.collapsed) {
    border-radius: 4px 4px 0 0;
  }

  .accordion-button::after {
    margin-left: auto;
  }

  .no-body .accordion-button::after {
    display: none;
  }

  .accordion-body {
    padding: 0;
  }
`;

const TimelineText = styled.div`
  padding: 12px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--x-gray-200, #f6f6f7);
  white-space: pre-wrap;
  background: var(--x-purple-500, #494358);
  border-radius: 0 0 4px 4px;
`;

const TimelineHeader = styled.div`
  padding: 12px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--x-gray-200, #f6f6f7);
  flex: 1;
  min-width: 0;
`;

type TraceRow = {
  time: string;
  traceId: string;
  service: string;
  serviceNotFound: boolean;
  division: string;
  policies: string[];
  violatedTypes: string[];
  maxRiskLevel: number;
  latencyMs?: number;
};

const splitMessage = (message: string) => {
  const [firstLine, ...rest] = message.split("\n");
  return {
    header: firstLine ?? "",
    body: rest.join("\n"),
  };
};

const TRACE_PAGE_SIZE = 100;

type ThreatTraceSectionProps = {
  hideApplicationColumn?: boolean;
  serviceTokenId?: string;
};

const ThreatTraceSection: FC<ThreatTraceSectionProps> = ({
  hideApplicationColumn = false,
  serviceTokenId,
}) => {
  const dispatch = useDispatch();
  const auth = useAuth();
  const { t } = useTranslation("dashboard");
  const { t: tPolicies } = useTranslation("policies");
  const dashboardState = useSelector(
    (state: RootState) => state.dashboard,
  );
  const serviceTokens = useSelector(
    (state: RootState) => state.infra?.serviceTokens,
  );
  const policiesData = useSelector(
    (state: RootState) => state.policies?.list?.data,
  ) as InternalPolicyItem[] | null;
  const traceRecords = dashboardState?.traceRecords;
  const statsSummary = dashboardState?.statsSummary;
  const licenseTimezone = dashboardState?.license?.results?.license_info?.timezone;
  const getPolicyDisplayName = (name: string) => {
    const localized = tPolicies(`${name}.name`, { defaultValue: "" });
    return localized || POLICY_DISPLAY_NAMES[name] || t("customPolicyFallback", { name });
  };

  const policyRiskMap = useMemo(() => {
    const map: Record<string, number> = { ...POLICY_RISK_LEVEL };
    if (policiesData) {
      policiesData.forEach((p) => { map[p.policy_name] = p.risk_level; });
    }
    return map;
  }, [policiesData]);

  const getPolicyTagIndex = (policyName: string): number => POLICY_TAG_COLOR_INDEX[policyName] ?? 3;

  const [isTraceOpen, setIsTraceOpen] = useState(false);
  const [selectedTrace, setSelectedTrace] = useState<TraceRow | null>(null);
  const [filterService, setFilterService] = useState<string[]>([]);
  const [filterPolicies, setFilterPolicies] = useState<string[]>([]);

  const serviceTokensResults: ServiceTokenType[] = Array.isArray(
    serviceTokens?.results,
  )
    ? serviceTokens.results
    : [];

  const dispatchFetch = useCallback((cursor?: string) => {
    if (!auth.user?.access_token) return;
    const params: Parameters<typeof getTraceRecords>[0] = {
      accessToken: auth.user.access_token,
      limit: TRACE_PAGE_SIZE,
      cursor,
    };
    if (serviceTokenId) {
      params.service_token_id = serviceTokenId;
    } else if (filterService.length > 0) {
      const tokenId = serviceTokensResults.find(
        (tok) => tok.service_token_name === filterService[0],
      )?.service_token_id;
      if (tokenId) params.service_token_id = tokenId;
    }
    if (filterPolicies.length > 0) {
      params.violated_policy_names = filterPolicies.join(",");
    }
    dispatch(getTraceRecords(params));
  }, [auth.user?.access_token, serviceTokenId, filterService, filterPolicies, serviceTokensResults, dispatch]);

  useEffect(() => {
    dispatch(resetTraceRecords());
    dispatchFetch();
    if (auth.user?.access_token) {
      dispatch(getStatsSummary({
        accessToken: auth.user.access_token,
        ...(serviceTokenId ? { service_token_id: serviceTokenId } : {}),
      }));
    }
  }, [filterService, filterPolicies, serviceTokenId]);

  const handleLoadMore = useCallback(() => {
    if (traceRecords?.loading || !traceRecords?.hasNext) return;
    dispatchFetch(traceRecords.nextCursor);
  }, [traceRecords?.loading, traceRecords?.hasNext, traceRecords?.nextCursor, dispatchFetch]);

  const traceResults: MaliciousRequest[] = traceRecords?.records ?? [];

  const mappedData: TraceRow[] = useMemo(() => traceResults.map((req) => {
    const serviceToken = serviceTokensResults.find(
      (token) => token.service_token_id === req.service_token_id,
    );
    const serviceName = serviceToken?.service_token_name || req.service_token_id;
    const serviceNotFound = !serviceToken;
    const time = formatTimestamp(req.timestamp, licenseTimezone);
    const policies: string[] = Array.isArray(req.violated_policy_names)
      ? req.violated_policy_names
      : [];

    const maxRiskLevel = policies.reduce((max, pName) => {
      const level = policyRiskMap[pName] ?? 0;
      return level > max ? level : max;
    }, 0);

    return {
      time,
      traceId: req.trace_id,
      service: serviceName,
      serviceNotFound,
      division: serviceToken?.division ?? "",
      policies,
      violatedTypes: Array.isArray(req.violated_types) ? req.violated_types : [],
      maxRiskLevel,
      latencyMs: req.latency_ms,
    };
  }), [traceResults, serviceTokensResults, licenseTimezone, policyRiskMap]);

  const traceDetail = dashboardState?.traceDetail?.results ?? null;

  const tablePageSize = mappedData.length || 10;
  const traceTitle = useMemo(() => {
    if (!selectedTrace?.service) return t("threats.modal.securityTrace");
    return t("threats.modal.securityTraceWithService", { service: selectedTrace.service });
  }, [selectedTrace?.service, t]);

  const traceTime = traceDetail?.timestamp
    ? formatTimestamp(traceDetail.timestamp, licenseTimezone)
    : selectedTrace?.time ?? "";
  const traceDivision = selectedTrace?.division || t("uncategorized");
  const traceId = traceDetail?.trace_id ?? selectedTrace?.traceId ?? "";
  const violations: TraceViolationItem[] = traceDetail?.scan_result?.reasons ?? [];
  const messages: TraceMessage[] = traceDetail?.scanned_messages ?? [];

  const evidencesByMessage = useMemo(() => {
    const map = new Map<number, EvidenceItem[]>();
    violations.forEach((v) => {
      if (v.evidence_status !== "READY" || !v.evidence) return;
      v.evidence.forEach((e) => {
        const list = map.get(e.message_index) ?? [];
        list.push(e);
        map.set(e.message_index, list);
      });
    });
    return map;
  }, [violations]);

  return (
    <TraceTableWrapper>
      <SectionHeader>{t("threats.sectionTitle")}</SectionHeader>

      <div style={{ backgroundColor: "var(--basicWrap)", padding: "12px" }}>
        <div className="d-flex justify-content-between mb-3">
          <div
            className="mb-3"
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            <IconWrapper>
              <Defend color="var(--x-gray-200)" />
            </IconWrapper>
            <span style={{ fontSize: 14, fontWeight: "bold" }}>
              {t("threats.tableHeading")}
            </span>
            <RecentUnsafeCount count={statsSummary?.results?.request_unsafe_count ?? mappedData.length} />
          </div>

        </div>

        <TableV8
          data={mappedData}
          loading={traceRecords?.loading && mappedData.length === 0}
          pagination={{
            pageSize: tablePageSize,
          }}
          manualPagination
          onDoubleClick={(
            event: MouseEvent,
            row: { original?: TraceRow } | undefined,
          ) => {
            event.preventDefault();
            const original = row?.original;
            if (!original) return;
            setSelectedTrace(original);
            setIsTraceOpen(true);
            dispatch(getTraceDetail({
              accessToken: auth.user?.access_token ?? "",
              traceId: original.traceId,
            }));
          }}
          columns={[
            {
              id: "maxRiskLevel",
              accessorKey: "maxRiskLevel",
              header: t("threats.table.riskLevel"),
              size: 80,
              enableSorting: false,
              cell: (info: TableCellInfo) => {
                const level = info.getValue() as unknown as number;
                if (!RISK_LEVEL_MAP[level]) {
                  const row = (info as unknown as { row: { original: TraceRow } }).row.original;
                  if (row.policies.length > 0) {
                    return (
                      <Tooltip trigger="click" placement="top" overlayComponent={t("threats.tooltip.policyNotFound")}>
                        <span style={{ cursor: "pointer", display: "inline-flex" }}>
                          <Lock size={14} color="var(--x-gray-400, #cfd0d3)" />
                        </span>
                      </Tooltip>
                    );
                  }
                  return null;
                }
                return <RiskLevelTag level={level} />;
              },
            },
            {
              id: "time",
              accessorKey: "time",
              header: t("threats.table.timeOfRequest"),
              enableSorting: false,
            },
            ...(!hideApplicationColumn ? [{
              id: "service",
              accessorKey: "service",
              header: t("threats.table.application"),
              meta: {
                filterComponent: (f: FilterComponentProps) => (
                  <HeaderSelectFilter
                    {...f}
                    isMulti={false}
                    options={serviceTokensResults.map((tok) => ({
                      label: tok.service_token_name,
                      value: tok.service_token_name,
                      textValue: tok.service_token_name,
                    }))}
                    onChange={setFilterService}
                  />
                ),
              },
              cell: (info: TableCellInfo) => {
                const row = (info as unknown as { row: { original: TraceRow } }).row.original;
                return (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span>{row.service}</span>
                    {row.serviceNotFound && (
                      <Tooltip
                        trigger="click"
                        placement="top"
                        overlayComponent={t("threats.tooltip.applicationNotFound")}
                      >
                        <span style={{ cursor: "pointer", display: "inline-flex" }}>
                          <Lock size={14} color="var(--x-gray-400, #cfd0d3)" />
                        </span>
                      </Tooltip>
                    )}
                  </div>
                );
              },
            }] : []),
            {
              id: "policies",
              accessorKey: "policies",
              header: t("threats.table.violationPolicies"),
              meta: {
                filterComponent: (f: FilterComponentProps) => {
                  const policyOptions = [
                    ...Object.keys(POLICY_DISPLAY_NAMES).map((key) => {
                      const label = getPolicyDisplayName(key);
                      return { label, value: key, textValue: label };
                    }),
                    ...(policiesData ?? [])
                      .filter((p) => !POLICY_DISPLAY_NAMES[p.policy_name])
                      .map((p) => {
                        const label = getPolicyDisplayName(p.policy_name);
                        return { label, value: p.policy_name, textValue: label };
                      }),
                  ];
                  return (
                    <HeaderSelectFilter
                      {...f}
                      options={policyOptions}
                      onChange={setFilterPolicies}
                    />
                  );
                },
              },
              cell: (info: TableCellInfo) => {
                const values = info.getValue() as unknown as string[];
                const firstPolicy = values[0];
                return (
                  <div style={{
                    display: "flex", alignItems: "center", gap: 4, width: "100%",
                  }}
                  >
                    <div style={{
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                    >
                      {firstPolicy && (
                        <>
                          <ViolationPolicyTypeTag index={getPolicyTagIndex(firstPolicy)}>
                            {getPolicyDisplayName(firstPolicy)}
                          </ViolationPolicyTypeTag>
                          {!policyRiskMap[firstPolicy] && (
                            <Tooltip trigger="click" placement="top" overlayComponent={t("threats.tooltip.policyNotFound")}>
                              <span style={{ cursor: "pointer", display: "inline-flex", flexShrink: 0 }}>
                                <Lock size={14} color="var(--x-gray-400, #cfd0d3)" />
                              </span>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </div>
                    {values.length > 1 && (
                      <Dropdown align="end" className="no-caret" style={{ flexShrink: 0 }}>
                        <Dropdown.Toggle variant="secondary" size="sm" style={{ padding: "2px 6px", fontSize: 12 }}>
                          {values.length}
                        </Dropdown.Toggle>
                        <Dropdown.Menu popperConfig={{ strategy: "fixed" }} renderOnMount>
                          {values.map((policy) => (
                            <Dropdown.Item key={policy}>
                              <ViolationPolicyTypeTag index={getPolicyTagIndex(policy)}>
                                {getPolicyDisplayName(policy)}
                              </ViolationPolicyTypeTag>
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    )}
                  </div>
                );
              },
            },
            {
              id: "latencyMs",
              accessorKey: "latencyMs",
              header: t("threats.table.avgLatency"),
              size: 120,
              enableSorting: false,
              cell: (info: TableCellInfo) => {
                const value = info.getValue() as unknown as number | undefined;
                return value != null ? `${value} ms` : "—";
              },
            },
            {
              id: "traceId",
              accessorKey: "traceId",
              header: t("threats.table.traceId"),
              enableSorting: false,
            },
            {
              id: "action",
              header: "",
              size: 28,
              minSize: 28,
              maxSize: 28,
              enableResizing: false,
              enableSorting: false,
              meta: {
                hideDropdown: true,
              },
              cell: () => (
                <IconAction>
                  <Document color="var(--x-gray-200, #F6F6F7)" />
                </IconAction>
              ),
            },
          ]}
          controlBarPosition="none"
        />
        <InfiniteLoader
          loading={traceRecords?.loading && mappedData.length > 0}
          hasMore={traceRecords?.hasNext ?? false}
          onLoadMore={handleLoadMore}
          observerRoot={null}
        />
      </div>
      <TraceModal
        show={isTraceOpen}
        setShow={setIsTraceOpen}
        restoreFocus={false}
        title={t("threats.modal.title")}
        cancelText={t("actions.close", { ns: "common", defaultValue: "Close" })}
        hidePrimaryButton
        hideCancelButton={false}
        showCloseBtn
      >
        <ModalSection>
          <ModalTitle>
            {traceTitle}
            {selectedTrace?.serviceNotFound && (
              <Tooltip
                trigger="click"
                placement="top"
                overlayComponent={t("threats.tooltip.applicationNotFound")}
              >
                <span style={{
                  cursor: "pointer", display: "inline-flex", marginLeft: 6, verticalAlign: "middle",
                }}
                >
                  <Lock size={16} color="var(--x-gray-400, #cfd0d3)" />
                </span>
              </Tooltip>
            )}
          </ModalTitle>
          <ModalSubRow>
            <ModalSubText>{traceDivision}</ModalSubText>
            <ModalSubText>{traceTime}</ModalSubText>
          </ModalSubRow>
        </ModalSection>
        <ModalSection>
          <KeyValueRow>
            <KeyLabel>{t("threats.modal.traceIdLabel")}</KeyLabel>
            <MonoText>{traceId}</MonoText>
          </KeyValueRow>
          <div style={{ height: 6 }} />
          <KeyValueRow>
            <KeyLabel>{t("threats.modal.latency")}</KeyLabel>
            <ValueText>
              {traceDetail?.latency_ms != null ? `${traceDetail.latency_ms} ms` : "-"}
            </ValueText>
          </KeyValueRow>
          {violations.length > 0 && (
            <>
              <div style={{ height: 12 }} />
              <div
                style={{
                  color: "var(--x-gray-500)",
                  fontSize: 14,
                  lineHeight: "1.5",
                }}
              >
                {t("threats.modal.violationDetails")}
              </div>
              <div style={{ height: 6 }} />
              {violations.map((v, idx) => {
                const severityLevel = policyRiskMap[v.violated_policy_name] ?? 0;
                return (
                  <div key={`${v.violated_type}-${idx}`}>
                    {idx > 0 && <div style={{ height: 6 }} />}
                    <ViolationCard>
                      <ViolationHeader>
                        {severityLevel > 0 && (
                          <RiskLevelTag level={severityLevel} />
                        )}
                        <ViolationTitle>{getPolicyDisplayName(v.violated_policy_name)}</ViolationTitle>
                      </ViolationHeader>
                      <div style={{ paddingLeft: 18 }}>
                        <div style={{ height: 3 }} />
                        <ViolationDetailRow>
                          <KeyLabel>{t("threats.modal.rationale")}</KeyLabel>
                          <ValueText>{v.rationale}</ValueText>
                        </ViolationDetailRow>
                      </div>
                    </ViolationCard>
                  </div>
                );
              })}
            </>
          )}
        </ModalSection>
        {messages.length > 0 && (
          <>
            <SectionHeading>{t("threats.modal.historyMessages")}</SectionHeading>
            <ModalSection>
              <TimelineWrap>
                {messages.map((msg, idx) => {
                  const role = msg.role as "system" | "user" | "assistant";
                  const toneMap: Record<string, "system" | "user" | "assistant" | "blocked"> = {
                    system: "system",
                    user: "user",
                    assistant: "assistant",
                  };
                  const iconEl: Record<string, JSX.Element> = {
                    system: <AIAction size={14} color="white" />,
                    user: <User size={14} color="white" />,
                    assistant: <AIChat size={14} color="white" />,
                  };
                  const roleLabel = role.charAt(0).toUpperCase() + role.slice(1);
                  const split = splitMessage(msg.content ?? "");
                  const msgEvidences = evidencesByMessage.get(idx) ?? [];
                  const headerLen = split.header.length;
                  const bodyStart = headerLen + 1;
                  const headerHighlights: HighlightRange[] = [];
                  const bodyHighlights: HighlightRange[] = [];
                  msgEvidences.forEach((e) => {
                    if (e.span.start < headerLen) {
                      headerHighlights.push({
                        start: e.span.start,
                        end: Math.min(e.span.end, headerLen),
                        type: e.type,
                      });
                    }
                    if (e.span.end > bodyStart) {
                      bodyHighlights.push({
                        start: Math.max(e.span.start, bodyStart) - bodyStart,
                        end: e.span.end - bodyStart,
                        type: e.type,
                      });
                    }
                  });
                  return (
                    <TimelineItem key={`${role}-${idx}`}>
                      <TimelineIcon tone={toneMap[role] ?? "system"}>
                        {iconEl[role] ?? <AIAction size={14} color="white" />}
                      </TimelineIcon>
                      <TimelineContent>
                        <TimelineRole>{roleLabel}</TimelineRole>
                        <HistoryAccordion defaultActiveKey={[]}>
                          <Accordion.Item
                            eventKey={`${role}-${idx}`}
                            className={split.body ? "" : "no-body"}
                          >
                            <Accordion.Header>
                              <TimelineHeader>
                                <HighlightedText text={split.header} highlights={headerHighlights} />
                              </TimelineHeader>
                            </Accordion.Header>
                            {split.body && (
                              <Accordion.Body>
                                <TimelineText>
                                  <HighlightedText text={split.body} highlights={bodyHighlights} />
                                </TimelineText>
                              </Accordion.Body>
                            )}
                          </Accordion.Item>
                        </HistoryAccordion>
                      </TimelineContent>
                    </TimelineItem>
                  );
                })}
              </TimelineWrap>
            </ModalSection>
          </>
        )}
      </TraceModal>
    </TraceTableWrapper>
  );
};

export default ThreatTraceSection;
